import { useState, type FormEvent } from "react";
import { useParams } from "react-router-dom";
import {
  useContribute,
  useCreateGoal,
  useDeleteGoal,
  useGoals,
  useSetAchieved,
  type GoalProgress,
} from "@/api/goals";
import { useHousehold } from "@/app/useHousehold";
import { GoalBar } from "@/components/GoalBar";
import { AmountError } from "@/money";
import {
  EmptyState,
  ErrorNote,
  Field,
  Loading,
  PageTitle,
  Panel,
  Tag,
  buttonClass,
  inputClass,
  quietButtonClass,
  rowsClass,
  secondaryButtonClass,
} from "@/components/ui";
import { violationsOf } from "@/i18n/apiMessages";
import { useFormat } from "@/i18n/format";
import { useT } from "@/i18n/LocaleProvider";

/**
 * Savings goals, with their progress (`model.md` §3.8, §4.3).
 *
 * The screen is small on purpose and rests on two things.
 *
 * **Progress is derived.** Nothing here is stored: every number on this page is
 * the sum of the goal's contributions divided by its target, computed when the
 * page is drawn. That is why raising a target moves the bar without anything
 * being written, and why deleting a booking takes its money back out.
 *
 * **Finished is a decision.** A goal at 100% is not marked done, and a goal
 * marked done need not be at 100% — bought second-hand, or given up on. The
 * button says "Als erreicht markieren" and it is the household's to press; the
 * bar says separately whether the money is there.
 */
export function GoalsPage() {
  const t = useT();
  const format = useFormat();
  const { householdId = "" } = useParams();
  const household = useHousehold();
  const currency = household?.base_currency_code ?? "EUR";

  const goals = useGoals(householdId);
  const create = useCreateGoal(householdId);
  const contribute = useContribute(householdId);
  const setAchieved = useSetAchieved(householdId);
  const remove = useDeleteGoal(householdId);

  const [name, setName] = useState("");
  const [target, setTarget] = useState("");
  const [targetDate, setTargetDate] = useState("");
  const [formError, setFormError] = useState<string>();

  const violation = violationsOf(t, create.error);

  function onSubmit(event: FormEvent) {
    event.preventDefault();
    setFormError(undefined);

    let minor: number;
    try {
      // A target is a magnitude, not a movement: no money moves in it, so the
      // sign convention of `model.md` §2.2 rule 1 does not apply.
      minor = Math.abs(format.parse(target, currency));
    } catch (err) {
      setFormError(err instanceof AmountError ? t(err.key, err.params) : String(err));
      return;
    }
    if (minor === 0) {
      setFormError(t("goals.targetNotZero"));
      return;
    }

    create.mutate(
      {
        name,
        target_amount_minor: minor,
        ...(targetDate ? { target_date: targetDate } : {}),
      },
      { onSuccess: () => { setName(""); setTarget(""); setTargetDate(""); } },
    );
  }

  const rows = goals.data?.items ?? [];

  return (
    <div>
      <PageTitle>{t("nav.goals")}</PageTitle>

      {goals.isLoading && <Loading />}
      <ErrorNote error={goals.error} />
      <ErrorNote error={contribute.error} />
      <ErrorNote error={setAchieved.error} />
      <ErrorNote error={remove.error} />

      {!goals.isLoading && rows.length === 0 && (
        <EmptyState title={t("goals.emptyTitle")}>{t("goals.emptyBody")}</EmptyState>
      )}

      {rows.length > 0 && (
        <ul className={`mt-6 ${rowsClass}`}>
          {rows.map((row) => (
            <li
              key={row.goal.id}
              data-testid="goal-row"
              className={`py-4 ${row.goal.is_achieved ? "opacity-75" : ""}`}
            >
              <GoalRow
                progress={row}
                currency={currency}
                pending={contribute.isPending || setAchieved.isPending || remove.isPending}
                onContribute={(amountMinor) =>
                  contribute.mutate({ goalId: row.goal.id, amountMinor })
                }
                onAchieved={() =>
                  setAchieved.mutate({ goalId: row.goal.id, achieved: !row.goal.is_achieved })
                }
                onRemove={() => remove.mutate(row.goal.id)}
              />
            </li>
          ))}
        </ul>
      )}

      <Panel className="mt-8 max-w-md">
        <form onSubmit={onSubmit} noValidate>
          <p className="text-sm font-medium text-ink">{t("goals.add")}</p>

          <Field label={t("goals.what")} htmlFor="goal-name" error={violation("name")}>
            <input
              id="goal-name"
              name="name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={t("goals.namePlaceholder")}
              className={`${inputClass} min-h-11 text-base sm:text-sm`}
            />
          </Field>

          <Field
            label={`${t("goals.target")} (${currency})`}
            htmlFor="goal-target"
            hint={t("goals.targetHint")}
            error={formError ?? violation("target_amount_minor")}
          >
            <input
              id="goal-target"
              name="target_amount_minor"
              value={target}
              onChange={(e) => setTarget(e.target.value)}
              inputMode="decimal"
              placeholder={format.plain(200_000, currency)}
              className={`${inputClass} num min-h-11 text-base sm:text-sm`}
            />
          </Field>

          <Field
            label={`${t("goals.byWhen")} (${t("common.optional")})`}
            htmlFor="goal-date"
            hint={t("goals.byWhenHint")}
            error={violation("target_date")}
          >
            <input
              id="goal-date"
              name="target_date"
              type="date"
              value={targetDate}
              onChange={(e) => setTargetDate(e.target.value)}
              className={`${inputClass} min-h-11 text-base sm:text-sm`}
            />
          </Field>

          <button
            type="submit"
            disabled={create.isPending}
            className={`${buttonClass} mt-4 min-h-11`}
          >
            {create.isPending ? t("onboarding.creating") : t("common.create")}
          </button>
          <ErrorNote error={create.error} />
        </form>
      </Panel>
    </div>
  );
}

function GoalRow({
  progress,
  currency,
  pending,
  onContribute,
  onAchieved,
  onRemove,
}: {
  progress: GoalProgress;
  currency: string;
  pending: boolean;
  onContribute: (amountMinor: number) => void;
  onAchieved: () => void;
  onRemove: () => void;
}) {
  const t = useT();
  const format = useFormat();
  const [amount, setAmount] = useState("");
  const [error, setError] = useState<string>();

  function put(event: FormEvent) {
    event.preventDefault();
    setError(undefined);
    try {
      // Positive from the pot's point of view: this form puts money in. Taking
      // it back out is deleting the contribution, not typing a minus.
      const minor = Math.abs(format.parse(amount, currency));
      if (minor === 0) {
        setError(t("goals.contributionNotZero"));
        return;
      }
      onContribute(minor);
      setAmount("");
    } catch (err) {
      setError(err instanceof AmountError ? t(err.key, err.params) : String(err));
    }
  }

  return (
    <div>
      <GoalBar
        label={progress.goal.name}
        savedMinor={progress.saved_minor}
        targetMinor={progress.goal.target_amount_minor}
        currency={currency}
        ratio={progress.ratio}
        displayRatio={progress.display_ratio}
        targetReached={progress.target_reached}
        isAchieved={progress.goal.is_achieved}
        monthlyPaceMinor={progress.monthly_pace_minor}
      />

      <div className="mt-2 flex flex-wrap items-center gap-2">
        {progress.goal.is_achieved && <Tag>{t("goal.achieved")}</Tag>}

        {!progress.goal.is_achieved && (
          <form onSubmit={put} noValidate className="flex items-center gap-2">
            <label htmlFor={`goal-put-${progress.goal.id}`} className="sr-only">
              {t("goals.payInFor", { name: progress.goal.name })}
            </label>
            <input
              id={`goal-put-${progress.goal.id}`}
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              inputMode="decimal"
              placeholder={format.plain(5_000, currency)}
              aria-label={t("goals.payInFor", { name: progress.goal.name })}
              className={`${inputClass} num mt-0 min-h-11 w-28 text-base sm:text-sm`}
            />
            <button
              type="submit"
              disabled={pending}
              className={`${buttonClass} min-h-11 px-3 text-xs`}
            >
              {t("goals.payIn")}
            </button>
          </form>
        )}

        <button
          type="button"
          onClick={onAchieved}
          disabled={pending}
          className={`${secondaryButtonClass} min-h-11 px-3 text-xs`}
          data-testid="goal-achieve"
        >
          {progress.goal.is_achieved ? t("goals.unachieve") : t("goals.markAchieved")}
        </button>

        <button
          type="button"
          onClick={onRemove}
          disabled={pending}
          className={`${quietButtonClass} min-h-11`}
        >
          {t("goals.remove")}
        </button>
      </div>

      {error && (
        <p className="mt-1 text-xs text-overspend" role="alert">
          {error}
        </p>
      )}
    </div>
  );
}
